import * as React from "react";
import { Fragment, useMemo, useState } from "react";
import { CellContext, ColumnDef, createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { Chip, LinearProgress, Typography } from "@mui/material";
import { BtmRequestStatusChip } from "../btm-request-status-chip/btm-request-status-chip";
import { BtmReturnRequestListItem } from "../model/btm-return-request-list-item";
import { BtmReturnRequestStatus } from "../model/btm-return-request-status";
import { BtmReturnRequestType } from "../model/btm-return-request-type";
import { BtmReturnRequestCategory } from "../model/btm-return-request-category";
import { t } from "i18next";

interface IBTMRequestTableFilter {
    quickSearch?: string;
    status?: BtmReturnRequestStatus;
    type?: BtmReturnRequestType;
    category?: BtmReturnRequestCategory;
}

export const useBtmRequestTable = (data: BtmReturnRequestListItem[], withStatus: boolean,
                                   withActionColumn: boolean, endCursor: number, filter: IBTMRequestTableFilter) => {
    const [columnVisibility, setColumnVisibility] = useState({ rawStatus: false, rawType: false, rawCategory: false, rawReturnDemandCode: false });

    const columnHelper = createColumnHelper<BtmReturnRequestListItem>();
    const BtmReturnRequestColumns = useMemo<Array<ColumnDef<BtmReturnRequestListItem, any>>>(() => [
        columnHelper.accessor("declarationId", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.declaration")}</Typography>,
            cell: info => <Fragment>
                <Typography variant={"h5"}>
                    {info.getValue()}
                </Typography>
                <Typography color={"textSecondary"}>
                    <span>{t("components.blueBrandReturns.list.columns.order")}: {info.row.original.orderId}</span><br />
                    {info.row.original.parcelNumber &&
                        <span>{t("components.blueBrandReturns.list.columns.parcel")}: {info.row.original.parcelNumber}</span>}
                </Typography>
            </Fragment>,
        }),
        columnHelper.accessor("operationCode", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.operationCode")}</Typography>,
            cell: info => <Typography>{info.getValue()}</Typography>,
        }),
        columnHelper.accessor("customerName", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.customer")}</Typography>,
            cell: info => <Typography>{info.getValue()}</Typography>,
        }),
        columnHelper.accessor("carrier", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.carrier")}</Typography>,
            cell: info => <Typography>{info.getValue()}</Typography>,
        }),
        ...(withStatus ? [columnHelper.accessor("status", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.status")}</Typography>,
            cell: ({ row }) =>
                <BtmRequestStatusChip status={row.original.status} />,
            filterFn: "equals" })] : []),
        columnHelper.accessor("returnType", {
            header: () => <Typography>{t("components.blueBrandReturns.list.columns.type")}</Typography>,
            cell: info => <Fragment>
                <Chip label={t(`components.blueBrandReturns.category.${info.row.original.returnCategory}`)}
                      size={"small"} variant={"outlined"} sx={{ marginLeft: 0 }} />
                <Chip label={t(`components.blueBrandReturns.type.${info.row.original.returnType}`)}
                      size={"small"} variant={"outlined"} sx={{ marginLeft: 0 }} />
            </Fragment>,
            filterFn: "equals",
        }),
        {
            id: "rawStatus",
            accessorKey: "status",
            header: () => null,
            cell: ({ row }) => row.original.status.toString(),
            filterFn: "equals",
        },
        {
            id: "rawType",
            accessorKey: "type",
            header: () => null,
            cell: ({ row }) => row.original.returnType.toString(),
            filterFn: "equals",
        },
        {
            id: "rawCategory",
            accessorKey: "category",
            header: () => null,
            cell: ({ row }) => row.original.returnCategory.toString(),
            filterFn: "equals",
        },
        {
            id: "rawReturnDemandCode",
            accessorKey: "returnDemandCode",
            header: () => null,
            cell: ({ row }) => row.original.returnDemandCode.toString(),
            filterFn: "equals",
        },
        ...(withActionColumn ? [{
            id: "actions",
            header: () => null,
            cell: ({ row }: CellContext<BtmReturnRequestListItem, any>) =>
                row.original.status === "New" ?
                    <Chip variant={"filled"} color={"primary"}label={t("components.blueBrandReturns.action.requiresCarrierDetails")} /> :
                    row.original.status === "Pending" ?
                        <Chip variant={"filled"} color={"primary"} label={t("components.blueBrandReturns.action.requiresTrackingDetails")} /> :
                        row.original.status === "Processed" ?
                            row.original.isInspectionInProgress ?
                                <ReturnRequestProgressAction item={row.original} /> :
                                <Chip variant={"filled"} color={"primary"} label={t("components.blueBrandReturns.action.readyToProcess")} /> :
                            <Fragment />,
        }] : []),
    ], []);

    const columnFilters = useMemo(() => [
        ...(filter.status ? [{ id: "rawStatus", value: filter.status.toString() }] : []),
        ...(filter.type ? [{ id: "rawType", value: filter.type.toString() }] : []),
        ...(filter.category ? [{ id: "rawCategory", value: filter.category.toString() }] : []),
    ], [filter]);

    const dataSorted = useMemo(() => {
        const getSortPriority = (item: BtmReturnRequestListItem) => {
            return item.status === "Processed" && item.isInspectionInProgress ? 0
                : item.status === "New" ? 1
                    : item.status === "Pending" ? 2
                        : item.status === "Processed" ? 3
                            : 4;
        };

        return data?.slice().sort((a, b) => getSortPriority(a) - getSortPriority(b));
    }, [data]);

    return useReactTable({
        data: dataSorted ?? [],
        state: {
            pagination: { pageIndex: 0, pageSize: endCursor },
            columnFilters,
            globalFilter: filter.quickSearch,
            columnVisibility,
        },
        columns: BtmReturnRequestColumns,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn: (row, _, filterValue) => {
            const search = String(filterValue).toLowerCase();
            return String(row.getValue("customerName") ?? "").toLowerCase().includes(search)
                || String(row.getValue("declarationId") ?? "").toLowerCase().includes(search)
                || String(row.original.orderId ?? "").toLowerCase().includes(search)
                || String(row.original.parcelNumber ?? "").toLowerCase().includes(search)
                || String(row.original.operationCode ?? "").toLowerCase().includes(search)
                || String(row.original.returnDemandCode ?? "").toLowerCase().includes(search);
        },
        manualPagination: false,
    });
};

const ReturnRequestProgressAction = ({ item }: { item: BtmReturnRequestListItem }) => {
    return <div className={"blue-request-progress__container"}>
        <Chip variant={"filled"} color={"primary"} label={t("components.blueBrandReturns.action.continue")}/>
        <LinearProgress variant={"determinate"}
                        value={(item.inspectedDeclaredItemsQuantity / item.declarationItemsCount) * 100}/>
    </div>;
};
